export const WEFAT_RANDOM_VALUES_API_NAMES = Object.freeze(['getRandomValues']);
export class WxRandomValuesRuntimeError extends Error {
    api = 'getRandomValues';
    code;
    errCode;
    errMsg;
    remediation;
    constructor(code, reason, remediation, cause) {
        const errMsg = `getRandomValues:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxRandomValuesRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.remediation = remediation;
        if (cause !== undefined)
            Object.defineProperty(this, 'cause', { value: cause });
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const OPTION_KEYS = new Set([...CALLBACK_KEYS, 'length']);
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function optionError(reason) {
    return new WxRandomValuesRuntimeError('WF6211', reason, 'Pass an integer length from 1 through 65536 with callable success, fail, and complete callbacks.');
}
function environmentError(reason, cause) {
    if (cause instanceof WxRandomValuesRuntimeError)
        return cause;
    return new WxRandomValuesRuntimeError('WF6210', reason, 'Run the generated application in a secure browser context with Web Crypto support.', cause);
}
function readOptions(raw) {
    if (!isPlainRecord(raw))
        throw optionError('options must be a plain object');
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !OPTION_KEYS.has(key)) {
            throw optionError(`unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(`${key} must be a function when provided`);
        }
    }
    if (!Number.isInteger(raw.length) || raw.length < 1 || raw.length > 65_536) {
        throw optionError('length must be an integer from 1 through 65536');
    }
    return raw;
}
function invokeCallback(callback, value, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, 'getRandomValues', kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
function browserEnvironment() {
    return {
        randomValues(length) {
            if (typeof globalThis.crypto?.getRandomValues !== 'function') {
                throw environmentError('Web Crypto random values are unavailable');
            }
            const bytes = new Uint8Array(length);
            globalThis.crypto.getRandomValues(bytes);
            return bytes.buffer;
        },
    };
}
export function createBrowserRandomValuesWxApis(options = {}) {
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    const environment = options.environment ?? browserEnvironment();
    if (!isPlainRecord(environment) || typeof environment.randomValues !== 'function') {
        throw new TypeError('createBrowserRandomValuesWxApis requires a Web Crypto or injected environment');
    }
    return Object.freeze({
        getRandomValues(raw) {
            const callbacks = readOptions(raw);
            const task = Promise.resolve().then(() => {
                try {
                    const randomValues = environment.randomValues(callbacks.length);
                    if (!(randomValues instanceof ArrayBuffer) || randomValues.byteLength !== callbacks.length) {
                        throw environmentError('random provider returned an invalid ArrayBuffer');
                    }
                    return Object.freeze({ errMsg: 'getRandomValues:ok', randomValues });
                }
                catch (error) {
                    throw environmentError(error instanceof Error ? error.message : 'Web Crypto random generation failed', error);
                }
            });
            if (callbacks.success === undefined && callbacks.fail === undefined && callbacks.complete === undefined) {
                return task;
            }
            void task.then((result) => {
                invokeCallback(callbacks.success, result, 'success', options.onCallbackError);
                invokeCallback(callbacks.complete, result, 'complete', options.onCallbackError);
            }, (error) => {
                invokeCallback(callbacks.fail, error, 'fail', options.onCallbackError);
                invokeCallback(callbacks.complete, error, 'complete', options.onCallbackError);
            });
            return undefined;
        },
    });
}
//# sourceMappingURL=random-values-adapter.js.map